import { ISchedule, IScheduleEntry, Schedule } from "@auvious/utils";
import dayjs from "./dayjs";

/**
 * Helpers around the `Schedule` class of @auvious/utils, so the open/closed business
 * hours authored in settings can be enforced on the scheduling screens the same way the
 * widget enforces them.
 *
 * Timezone contract: `Schedule` interprets its year/month/day/hour arguments in the
 * *browser's local* timezone and re-projects them into the configured `timeZone`. So the
 * way to ask "is this instant open?" is to pass the local calendar fields of that
 * instant — which is what every function here does, taking plain `Date` instants. Never
 * feed it the wall-clock of some other zone (e.g. `dayjs.tz(...).hour()`): that is
 * silently wrong by the offset between the two zones.
 */

const MINUTES_PER_DAY = 24 * 60;
const MS_PER_MINUTE = 60000;

/** per-schedule cache of "is minute N of this local day open", keyed by local day */
const dayCache = new WeakMap<Schedule, Map<number, boolean[]>>();

export function isScheduleConfigured(config?: ISchedule): boolean {
  return (
    !!config &&
    (config.schedule?.open?.length > 0 || config.schedule?.closed?.length > 0)
  );
}

/**
 * @returns a `Schedule` for the given configuration, or null when nothing is configured.
 *          A null schedule means "always open", which every function here honours.
 */
export function createSchedule(config?: ISchedule): Schedule | null {
  if (!isScheduleConfigured(config)) return null;

  return new Schedule({
    // an absent timeZone makes Intl fall back to the browser zone, so the same schedule
    // would mean different hours per visitor — anchor it explicitly
    timeZone: config.timeZone || dayjs.tz.guess(),
    schedule: config.schedule,
  });
}

/** minute-by-minute openness of the local calendar day containing `instant` */
function dayMap(schedule: Schedule, instant: Date): boolean[] {
  let cache = dayCache.get(schedule);
  if (!cache) {
    cache = new Map();
    dayCache.set(schedule, cache);
  }

  const year = instant.getFullYear();
  const month = instant.getMonth();
  const date = instant.getDate();
  const key = year * 10000 + month * 100 + date;

  const cached = cache.get(key);
  if (cached) return cached;

  // getMinutes returns the 60 minutes of the hour, in order
  const minutes: boolean[] = new Array(MINUTES_PER_DAY).fill(false);
  for (let hour = 0; hour < 24; hour++) {
    const units = schedule.getMinutes(year, month, date, hour);
    for (let i = 0; i < units.length && i < 60; i++) {
      minutes[hour * 60 + i] = units[i].open;
    }
  }

  cache.set(key, minutes);
  return minutes;
}

function isInstantOpen(schedule: Schedule, instant: Date): boolean {
  return dayMap(schedule, instant)[
    instant.getHours() * 60 + instant.getMinutes()
  ];
}

/** True when every minute of [start, start + duration) is open. */
export function isSlotOpen(
  schedule: Schedule,
  start: Date,
  durationMinutes: number
): boolean {
  if (!schedule) return true;

  const duration = Math.max(1, Math.round(durationMinutes) || 1);
  const from = start.getTime();
  for (let m = 0; m < duration; m++) {
    // walking instants (rather than minute indices) keeps this correct across the local
    // midnight boundary and across DST transitions
    if (!isInstantOpen(schedule, new Date(from + m * MS_PER_MINUTE))) {
      return false;
    }
  }
  return true;
}

/**
 * Bookable start instants between `from` (inclusive) and `to` (exclusive), stepped by
 * `stepMinutes`, keeping only those where the whole appointment fits inside open hours.
 */
export function openSlots(
  schedule: Schedule,
  from: Date,
  to: Date,
  stepMinutes: number,
  durationMinutes: number
): Date[] {
  const step = Math.max(1, Math.round(stepMinutes) || 1);
  const duration = Math.max(1, Math.round(durationMinutes) || 1);

  const slots: Date[] = [];
  for (
    let time = from.getTime();
    time < to.getTime();
    time += step * MS_PER_MINUTE
  ) {
    const slot = new Date(time);
    if (isSlotOpen(schedule, slot, duration)) slots.push(slot);
  }
  return slots;
}

/**
 * True when the interval holds at least one bookable slot. Mirrors the widget's calendar
 * rule: a day whose open runs are all shorter than the appointment is shown as closed
 * rather than as a day with an empty slot list.
 */
export function hasOpenSlot(
  schedule: Schedule,
  from: Date,
  to: Date,
  stepMinutes: number,
  durationMinutes: number
): boolean {
  if (!schedule) return true;

  const step = Math.max(1, Math.round(stepMinutes) || 1);
  const duration = Math.max(1, Math.round(durationMinutes) || 1);
  for (
    let time = from.getTime();
    time < to.getTime();
    time += step * MS_PER_MINUTE
  ) {
    if (isSlotOpen(schedule, new Date(time), duration)) return true;
  }
  return false;
}

/**
 * Reasons attached to the closed entries covering `instant`'s local day.
 * De-duplicated, in the order the schedule reports them.
 */
export function closedReasons(schedule: Schedule, instant: Date): string[] {
  if (!schedule) return [];
  const days = schedule.getDays(instant.getFullYear(), instant.getMonth());
  const reasons = days[instant.getDate() - 1]?.reasons ?? [];
  return [...new Set(reasons.filter(Boolean))];
}

/**
 * The instants bounding a calendar day as seen in `timezone` (the browser's zone when
 * none is given). Scheduling screens show days in the appointment's timezone, while the
 * schedule is queried with instants, so the two have to be bridged explicitly.
 */
export function dayBounds(day: dayjs.Dayjs, timezone?: string): [Date, Date] {
  const start = timezone
    ? dayjs.tz(day.format("YYYY-MM-DD"), timezone)
    : day.startOf("day");
  return [start.toDate(), start.add(1, "day").toDate()];
}

/* -------------------------------------------------------------------------- */
/* human-readable rendering                                                    */
/* -------------------------------------------------------------------------- */

const WEEKDAY_KEYS = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
];

type TranslateFn = (key: string) => string;

const identity: TranslateFn = (key) => key;

function asArray<T>(value: T | T[] | undefined): T[] {
  return Array.isArray(value) ? value : value ? [value] : [];
}

function doubleJoin(
  strs: string[],
  translate: TranslateFn,
  prefix = "",
  j1 = ", ",
  j2?: string
) {
  const last = j2 ?? ` ${translate("and")} `;
  switch (strs.length) {
    case 0:
      return "";
    case 1:
      return prefix + strs[0];
    case 2:
      return prefix + strs.join(last);
    default: {
      const rest = [...strs];
      const tail = rest.pop();
      return `${prefix}${rest.join(j1)} ${last} ${tail}`;
    }
  }
}

function formatDate(date: string) {
  if (!date) return "";
  // a year-less date recurs every year, so any year renders the same "MMM DD"
  return dayjs(date.length < 6 ? `2025-${date}` : date).format("MMM DD");
}

function formatTime(time: string, translate: TranslateFn) {
  // "24:00" is the schedule model's end-of-day marker
  return time === "24:00" ? translate("midnight") : time;
}

/**
 * Renders one schedule entry as a sentence, e.g.
 * "Mondays and Tuesdays, 09:00 to 17:00".
 */
export function renderScheduleSummary(
  entry: IScheduleEntry,
  translate: TranslateFn = identity
): string {
  const dateIntervals = asArray(entry.date).map((date) => date.split("/"));

  const singleDateStr = doubleJoin(
    dateIntervals
      .filter((d) => d.length === 1)
      .flat()
      .map(formatDate),
    translate,
    `${translate("on")} `
  );

  const rangeStr = doubleJoin(
    dateIntervals
      .filter((d) => d.length > 1)
      .map((d) => `${formatDate(d[0])} ${translate("to")} ${formatDate(d[1])}`),
    translate,
    `${translate("from")} `
  );

  const weekdays = asArray(entry.weekdays);
  const weekdaysStr = doubleJoin(
    weekdays.map((wd) => translate(WEEKDAY_KEYS[wd - 1])),
    translate,
    `${translate("on")} `
  );

  const timesStr = doubleJoin(
    asArray(entry.time).map((time) =>
      time
        .split("/")
        .map((t) => formatTime(t, translate))
        .join(` ${translate("to")} `)
    ),
    translate,
    weekdays.length > 0 ? "" : `${translate("daily from")} `
  );

  const sentence = doubleJoin(
    [singleDateStr, rangeStr, weekdaysStr, timesStr].filter(Boolean),
    translate,
    "",
    ", ",
    ", "
  );

  return sentence.length
    ? sentence.charAt(0).toUpperCase() + sentence.slice(1)
    : "";
}

/**
 * Renders a whole schedule as one line per entry, for a read-only summary. Closed lines
 * keep the entry's reason (e.g. "bank holiday"); open entries have none in the model.
 */
export function renderScheduleLines(
  config: ISchedule,
  translate: TranslateFn = identity
): { open: string[]; closed: { summary: string; reason?: string }[] } {
  return {
    open: (config?.schedule?.open ?? []).map((e) =>
      renderScheduleSummary(e, translate)
    ),
    closed: (config?.schedule?.closed ?? []).map((e) => ({
      summary: renderScheduleSummary(e, translate),
      reason: e.reason,
    })),
  };
}
